import React, { useState } from 'react';
import { Box, useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { motion } from 'framer-motion';

import Header from './Header';
import Sidebar from './Sidebar';
import MobileNav from '../Mobile/MobileNav';
import { useAppContext } from '../../context/AppContext';
import { useMobileDetect } from '../../hooks/useMobileDetect';

function Layout({ children, isMobile: propIsMobile }) {
  const theme = useTheme();
  const { 
    sidebarOpen, 
    setSidebarOpen, 
    darkMode, 
    toggleDarkMode,
    unreadCount,
    user 
  } = useAppContext();
  
  const { 
    isMobile, 
    shouldUseMobileLayout, 
    shouldUseBottomNavigation 
  } = useMobileDetect();
  
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  
  // Use prop or hook value
  const isActuallyMobile = propIsMobile !== undefined ? propIsMobile : shouldUseMobileLayout();
  const isLegacyMobile = useMediaQuery(theme.breakpoints.down('lg'));

  // Close sidebar on mobile by default
  React.useEffect(() => {
    if ((isActuallyMobile || isLegacyMobile) && sidebarOpen) {
      setSidebarOpen(false);
    }
  }, [isActuallyMobile, isLegacyMobile, sidebarOpen, setSidebarOpen]);

  const drawerWidth = 280;

  // Mock notifications for mobile nav
  const notifications = {
    dashboard: 0,
    photos: 0,
    calendar: 2,
    reviews: unreadCount || 5,
    analytics: 0,
  };

  const handleMobileNavOpen = () => setMobileNavOpen(true);
  const handleMobileNavClose = () => setMobileNavOpen(false);

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      {/* Header with mobile nav integration */}
      <Header 
        drawerWidth={drawerWidth}
        isMobile={isActuallyMobile}
        onMobileNavOpen={handleMobileNavOpen}
      />

      {/* Desktop Sidebar - Hidden on mobile */}
      {!isActuallyMobile && (
        <Sidebar
          drawerWidth={drawerWidth}
          open={sidebarOpen}
          onClose={() => setSidebarOpen(false)}
          isMobile={isLegacyMobile}
        />
      )}

      {/* Mobile Navigation Drawer */}
      {isActuallyMobile && (
        <MobileNav
          open={mobileNavOpen}
          onOpen={handleMobileNavOpen}
          onClose={handleMobileNavClose}
          darkMode={darkMode}
          onToggleDarkMode={toggleDarkMode}
          notifications={notifications}
          user={user || { name: 'KashLegal User', avatar: '' }}
        />
      )}

      {/* Main Content with mobile optimizations */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          pt: { 
            xs: isActuallyMobile ? 7 : 8, 
            sm: isActuallyMobile ? 7 : 9 
          }, // Reduced top padding for mobile
          pl: {
            xs: 0,
            lg: (!isActuallyMobile && sidebarOpen) ? `${drawerWidth}px` : 0,
          },
          pb: shouldUseBottomNavigation() ? 10 : 0, // Space for bottom nav
          transition: theme.transitions.create(['padding-left'], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.standard,
          }),
          minHeight: '100vh',
          backgroundColor: theme.palette.background.default,
          background: theme.palette.background.gradient,
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        {/* Animated background decoration for mobile */}
        {isActuallyMobile && (
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: `
                radial-gradient(circle at 20% 80%, rgba(30, 58, 138, 0.1) 0%, transparent 50%),
                radial-gradient(circle at 80% 20%, rgba(59, 130, 246, 0.08) 0%, transparent 50%),
                radial-gradient(circle at 40% 40%, rgba(220, 38, 38, 0.05) 0%, transparent 50%)
              `,
              pointerEvents: 'none',
              zIndex: -1,
            }}
          />
        )}

        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
        >
          <Box 
            sx={{ 
              p: { 
                xs: isActuallyMobile ? 1.5 : 2, 
                md: isActuallyMobile ? 2 : 3 
              }, 
              maxWidth: '100%',
              paddingLeft: { xs: '16px', sm: '24px' },
              paddingRight: { xs: '16px', sm: '24px' },
            }}
          >
            {children}
          </Box>
        </motion.div>
      </Box>
    </Box>
  );
}

export default Layout;